/**
 * Wiki manifest — table of contents structure and page loader.
 *
 * Exports:
 *   WIKI_SECTIONS  — array of section objects with nested pages
 *   getPageContent  — returns the markdown string for a given pageId
 *   DEFAULT_PAGE_ID — page shown on initial render
 *   getPageMeta     — returns page metadata (id, title, component flag)
 */

// Lazy-import page content to keep bundle split simple while avoiding
// waterfall network requests (each page is a small string export).
import phdOverview from './pages/phd-overview';
import phdTopologies from './pages/phd-topologies';
import phdSimulation from './pages/phd-simulation';
import phdResults from './pages/phd-results';
import phdComparison from './pages/phd-comparison';

import forecastingOverview from './pages/forecasting-overview';
import forecastingDataPipeline from './pages/forecasting-data-pipeline';
import forecastingTraining from './pages/forecasting-training';
import forecastingValidation from './pages/forecasting-validation';
import forecastingColdStart from './pages/forecasting-cold-start';

import conceptsGlossary from './pages/concepts-glossary';
import conceptsPop from './pages/concepts-pop';
import conceptsAdmm from './pages/concepts-admm';
import conceptsComponentModels from './pages/concepts-component-models';

// Architecture pages — hand-written
import archSystemOverview from './pages/arch-system-overview';
import archPhdApi from './pages/arch-phd-api';
import archSolver from './pages/arch-solver';

// Architecture pages — auto-generated from docs/architecture/*.md by tools/sync-wiki.py
import archSiteDna from './pages/arch-site-dna';
import archFluxEv from './pages/arch-flux-ev';
import archFluxCold from './pages/arch-flux-cold';
import archFluxEms from './pages/arch-flux-ems';
import archFluxPhd from './pages/arch-flux-phd';
import archOrchestrator from './pages/arch-orchestrator';
import archPlatformApi from './pages/arch-platform-api';
import archSharedUi from './pages/arch-shared-ui';
import archDigitalTwin from './pages/arch-digital-twin';

// ---------------------------------------------------------------------------
// Page registry — maps pageId to its markdown content string
// ---------------------------------------------------------------------------
const PAGE_MAP = {
  'phd-overview': phdOverview,
  'phd-topologies': phdTopologies,
  'phd-simulation': phdSimulation,
  'phd-results': phdResults,
  'phd-comparison': phdComparison,

  'forecasting-overview': forecastingOverview,
  'forecasting-data-pipeline': forecastingDataPipeline,
  'forecasting-training': forecastingTraining,
  'forecasting-validation': forecastingValidation,
  'forecasting-cold-start': forecastingColdStart,

  'concepts-glossary': conceptsGlossary,
  'concepts-pop': conceptsPop,
  'concepts-admm': conceptsAdmm,
  'concepts-component-models': conceptsComponentModels,

  'arch-system-overview': archSystemOverview,
  'arch-phd-api': archPhdApi,
  'arch-solver': archSolver,
  'arch-site-dna': archSiteDna,
  'arch-flux-ev': archFluxEv,
  'arch-flux-cold': archFluxCold,
  'arch-flux-ems': archFluxEms,
  'arch-flux-phd': archFluxPhd,
  'arch-orchestrator': archOrchestrator,
  'arch-platform-api': archPlatformApi,
  'arch-shared-ui': archSharedUi,
  'arch-digital-twin': archDigitalTwin,
};

// ---------------------------------------------------------------------------
// Table of contents
// ---------------------------------------------------------------------------
export const WIKI_SECTIONS = [
  {
    id: 'phd-guide',
    title: 'PhD Guide',
    pages: [
      { id: 'phd-overview', title: 'Overview' },
      { id: 'phd-topologies', title: 'Topologies' },
      { id: 'phd-simulation', title: 'Simulation' },
      { id: 'phd-results', title: 'Results' },
      { id: 'phd-comparison', title: 'Comparison' },
    ],
  },
  {
    id: 'forecasting',
    title: 'Forecasting',
    pages: [
      { id: 'forecasting-overview', title: 'Overview' },
      { id: 'forecasting-data-pipeline', title: 'Data Pipeline' },
      { id: 'forecasting-training', title: 'Model Training' },
      { id: 'forecasting-validation', title: 'Validation' },
      { id: 'forecasting-cold-start', title: 'Cold-Start Prediction' },
    ],
  },
  {
    id: 'concepts',
    title: 'Concepts',
    pages: [
      { id: 'concepts-glossary', title: 'Glossary' },
      { id: 'concepts-pop', title: 'Peak Optimization (PoP)' },
      { id: 'concepts-admm', title: 'ADMM Coordination' },
      { id: 'concepts-component-models', title: 'Component Models' },
    ],
  },
  {
    id: 'architecture',
    title: 'Architecture',
    pages: [
      { id: 'arch-platform-map', title: 'Platform Map', component: true },
      { id: 'arch-system-overview', title: 'System Overview' },
      { id: 'arch-platform-api', title: 'Platform API' },
      { id: 'arch-site-dna', title: 'Site DNA' },
      { id: 'arch-flux-ev', title: 'Flux EV' },
      { id: 'arch-flux-cold', title: 'Flux COLD' },
      { id: 'arch-flux-ems', title: 'Flux EMS' },
      { id: 'arch-flux-phd', title: 'Flux PhD' },
      { id: 'arch-orchestrator', title: 'Orchestrator' },
      { id: 'arch-shared-ui', title: 'Shared UI' },
      { id: 'arch-digital-twin', title: 'Digital Twin' },
      { id: 'arch-phd-api', title: 'PhD API' },
      { id: 'arch-solver', title: 'Solver' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const DEFAULT_PAGE_ID = 'phd-overview';

/**
 * Return the raw markdown string for a page, or null if not found.
 */
export function getPageContent(pageId) {
  return PAGE_MAP[pageId] ?? null;
}

/**
 * Return page metadata (id, title, component flag) for a given pageId, or null.
 */
export function getPageMeta(pageId) {
  for (const section of WIKI_SECTIONS) {
    const page = section.pages.find((p) => p.id === pageId);
    if (page) return page;
  }
  return null;
}
